const kids_shippingRequests = new Map();
const kids_shippingCache = new Map();
const kids_shippingCacheTtl = 15 * 60 * 1000;
const kids_shippingMadridFormatter = new Intl.DateTimeFormat( 'en-GB', {
	timeZone: 'Europe/Madrid', year: 'numeric', month: '2-digit', day: '2-digit',
	hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
} );

function kids_getShippingPeriod ( kids_timestamp ) {
	const kids_parts = Object.fromEntries( kids_shippingMadridFormatter.formatToParts( kids_timestamp ).map( kids_part => [ kids_part.type, kids_part.value ] ) );
	const kids_afterCutoff = Number( kids_parts.hour ) * 60 + Number( kids_parts.minute ) >= 10 * 60 + 30;

	return `${kids_parts.year}-${kids_parts.month}-${kids_parts.day}-${kids_afterCutoff}`;
}

function kids_areShippingDatesValid ( kids_dates ) {
	return [ kids_dates?.date_min, kids_dates?.date_max ].every( kids_date =>
		typeof kids_date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test( kids_date ) &&
		Number.isFinite( Date.parse( kids_date ) ) && new Date( kids_date ).toISOString().slice( 0, 10 ) === kids_date
	) && kids_dates.date_min <= kids_dates.date_max;
}

function kids_readShippingCache ( kids_cacheKey, kids_now ) {
	let kids_entry = kids_shippingCache.get( kids_cacheKey );

	if ( !kids_entry ) {
		try {
			kids_entry = JSON.parse( localStorage.getItem( kids_cacheKey ) );
		}
		catch {}
	}

	if ( !kids_entry ) return null;

	if ( !Number.isFinite( kids_entry.kids_createdAt ) || kids_entry.kids_createdAt > kids_now ||
		kids_now - kids_entry.kids_createdAt >= kids_shippingCacheTtl ||
		kids_getShippingPeriod( kids_entry.kids_createdAt ) !== kids_getShippingPeriod( kids_now ) ||
		!kids_areShippingDatesValid( kids_entry.kids_dates ) ) {
		kids_shippingCache.delete( kids_cacheKey );
		try {
			localStorage.removeItem( kids_cacheKey );
		}
		catch {}
		return null;
	}

	kids_shippingCache.set( kids_cacheKey, kids_entry );
	return kids_entry.kids_dates;
}

function kids_formatDateLocalized ( kids_date, kids_locale = 'es-ES' ) {
	return new Intl.DateTimeFormat( kids_locale, {
		day: '2-digit', month: 'short', timeZone: 'UTC'
	} ).format( kids_date );
}

function kids_formatDateRangeLocalized ( kids_dateMin, kids_dateMax, kids_locale = 'es-ES' ) {
	return new Intl.DateTimeFormat( kids_locale, {
		day: '2-digit', month: 'short', timeZone: 'UTC'
	} ).formatRange( kids_dateMin, kids_dateMax ).replace( /[–—-]/g, ' - ' );
}

async function kids_getShippingTime ( {
	kids_minDays = 2,
	kids_maxDays = 5,
	kids_locale = 'es-ES'
} = {} ) {
	const kids_startedAt = Date.now();
	const kids_cacheKey = `kids_shipping_dates_v1:${kids_minDays}-${kids_maxDays}`;
	const kids_requestKey = `${kids_cacheKey}:${kids_getShippingPeriod( kids_startedAt )}`;
	let kids_dates = kids_readShippingCache( kids_cacheKey, kids_startedAt );

	if ( !kids_dates ) {
		if ( !kids_shippingRequests.has( kids_requestKey ) ) {
			const kids_request = fetch( 'https://app.v2.ecuskids.com/shopify/utils/delivery-dates/get', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify( { days_min: kids_minDays, days_max: kids_maxDays } ),
				credentials: 'omit',
				cache: 'no-store',
				signal: AbortSignal.timeout( 25000 )
			} ).then( async kids_response => {
				const kids_payload = await kids_response.json();
				if ( !kids_response.ok ) throw new Error( `Shipping estimate request failed with status ${kids_response.status}` );
				if ( kids_payload?.success !== true ) throw new Error( 'Shipping estimate is unavailable' );
				if ( !kids_areShippingDatesValid( kids_payload.data ) ) throw new Error( 'Invalid shipping estimate response' );

				const kids_dates = { date_min: kids_payload.data.date_min, date_max: kids_payload.data.date_max };
				const kids_entry = { kids_createdAt: kids_startedAt, kids_dates };

				if ( !kids_shippingCache.has( kids_cacheKey ) || kids_shippingCache.get( kids_cacheKey ).kids_createdAt <= kids_startedAt ) {
					kids_shippingCache.set( kids_cacheKey, kids_entry );
					try {
						localStorage.setItem( kids_cacheKey, JSON.stringify( kids_entry ) );
					}
					catch {}
				}
				return kids_dates;
			} ).finally( () => kids_shippingRequests.delete( kids_requestKey ) );

			kids_shippingRequests.set( kids_requestKey, kids_request );
		}
		kids_dates = await kids_shippingRequests.get( kids_requestKey );
	}

	const kids_dateMin = new Date( `${kids_dates?.date_min}T00:00:00Z` );
	const kids_dateMax = new Date( `${kids_dates?.date_max}T00:00:00Z` );
	return {
		kids_min: kids_formatDateLocalized( kids_dateMin, kids_locale ),
		kids_max: kids_formatDateLocalized( kids_dateMax, kids_locale ),
		kids_range: kids_formatDateRangeLocalized( kids_dateMin, kids_dateMax, kids_locale ),
		kids_sameMonth: kids_dates.date_min.slice( 0, 7 ) === kids_dates.date_max.slice( 0, 7 )
	};
}

if ( !customElements.get( 'kids-product-shipping-info' ) ) {
	customElements.define(
		'kids-product-shipping-info',
		class kids_ProductShippingInfo extends HTMLElement {
			constructor () {
				super();
				this.kids_renderVersion = 0;
			}

			connectedCallback () {
				this.kids_renderVersion++;
				this.setAttribute( 'aria-busy', 'true' );
				this.kids_render( this.kids_renderVersion );
			}

			disconnectedCallback () {
				this.kids_renderVersion++;
			}

			async kids_render ( kids_renderVersion ) {
				const kids_message = this.dataset.message;

				if ( kids_message ) {
					this.innerHTML = `<span data-type="warning">${kids_message}</span>`;
					this.removeAttribute( 'aria-busy' );
					return;
				}

				const kids_minDays = /^(0|[1-9]\d*)$/.test( this.dataset.min || '' ) ? Number( this.dataset.min ) : NaN;
				const kids_maxDays = /^(0|[1-9]\d*)$/.test( this.dataset.max || '' ) ? Number( this.dataset.max ) : NaN;
				const kids_locale = this.dataset.locale || 'es-ES';
				const kids_template = this.dataset.template;
				const kids_templateSameMonth = this.dataset.templateSameMonth;
				const kids_tone = this.dataset.tone === 'warning' ? 'warning' : 'normal';

				try {
					const kids_validDays = Number.isInteger( kids_minDays ) && Number.isInteger( kids_maxDays ) && kids_minDays >= 0 && kids_maxDays >= kids_minDays && kids_maxDays <= 365;
					if ( !kids_validDays ) {
						throw new Error( 'Shipping estimate days are not available' );
					}

					const {
						kids_min,
						kids_max,
						kids_range,
						kids_sameMonth
					} = await kids_getShippingTime( {
						kids_minDays,
						kids_maxDays,
						kids_locale
					} );

					if ( !this.isConnected || kids_renderVersion !== this.kids_renderVersion ) {
						return;
					}

					const kids_dateMinHtml = `<strong class="kids_product-info-shipping-date">${kids_min}</strong>`;
					const kids_dateMaxHtml = `<strong class="kids_product-info-shipping-date">${kids_max}</strong>`;
					const kids_dateRangeHtml = `<strong class="kids_product-info-shipping-date">${kids_range}</strong>`;
					const kids_hasSameMonthTemplate = kids_templateSameMonth && !kids_templateSameMonth.includes( 'Translation missing' );
					const kids_result = kids_sameMonth && kids_hasSameMonthTemplate
						? kids_templateSameMonth.replace( '%s', kids_dateRangeHtml )
						: kids_template.replace( '%s', kids_dateMinHtml ).replace( '%s', kids_dateMaxHtml );
					const kids_content = this.dataset.messageAlt
						? `${this.dataset.messageAlt} ${kids_result}`
						: kids_result;

					this.innerHTML = `<span data-type="${kids_tone}">${kids_content}</span>`;
				}
				catch ( kids_error ) {
					if ( !this.isConnected || kids_renderVersion !== this.kids_renderVersion ) {
						return;
					}

					console.error( '[kids_shipping_info] Error calculando el plazo de entrega:', kids_error, { ...this.dataset, kids_minDays, kids_maxDays } );
					this.textContent = this.dataset.unavailable;
				}
				finally {
					if ( this.isConnected && kids_renderVersion === this.kids_renderVersion ) {
						this.removeAttribute( 'aria-busy' );
					}
				}
			}
		}
	);
}
